import { useMemo, useRef } from "react";

import { UsersIcon } from "lucide-react";

import { TutorialVideoCard } from "src/components/Analytics/TutorialVideoCard";
import { DataExportButton } from "src/components/dataView/DataExport/DataExportButton";
import { DataTable } from "src/components/dataView/DataTable/DataTable";
import {
  colorGroupColumn,
  colorGroupLabelColumn,
  getExcelExportData,
  getRecentMonths,
  styledDateHeader,
  transposeDataToRows,
} from "src/components/dataView/DataTable/tableHelpers";
import { MetricsFilter } from "src/components/dataView/MetricsTable/MetricsFilter";
import WaterfallChart from "src/components/dataView/WaterfallChart/WaterfallChart";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker";
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import { tutorialVideos } from "src/lib/constants";
import { unitEconomicsAtom, useAnalytics } from "src/lib/hooks/useAnalytics";
import { UNIT_ECONOMICS_QUERY } from "src/lib/queries/analytics";
import { cn } from "src/lib/utils";

const createRowMapper = (numberFormat) => (columns, data, returningCustomer) =>
  transposeDataToRows(
    columns,
    data,
    (d, col) => {
      return (
        d.period === col &&
        (returningCustomer === undefined || // undefined takes care of summary columns, it matches only by the column period
          d.returningCustomer === returningCustomer)
      );
    },
    (map) => [
      {
        label: "ORDERS",
        format: numberFormat.whole,
        ...map("orders"),
      },
      {
        label: "AVERAGE ORDER VALUE",
        meta: { color: "var(--primary)" },
        ...map("aov"),
        subRows: [
          {
            label: "Gross Retail",
            meta: {
              color: "var(--primary)",
              alwaysShow: true,
            },
            ...map("grossRetail"),
            subRows: [
              {
                label: "Average Unit Retail",
                ...map("aur"),
              },
              {
                label: "Units Per Transaction",
                format: numberFormat.whole,
                ...map("upt"),
              },
            ],
          },
          {
            label: "Discount Rate",
            format: numberFormat.percentTimes100,
            ...map("dr"),
          },
          {
            label: "Refund Rate",
            format: numberFormat.percentTimes100,
            ...map("rr"),
          },
          {
            label: "Shipping Collected",
            format: numberFormat.currency,
            ...map("shippingCollected"),
          },
          {
            label: "AVERAGE ORDER VALUE",
            meta: { highlight: true },
            ...map("aov"),
          },
        ],
      },
      {
        label: "GROSS PROFIT",
        meta: { color: "var(--primary)" },
        ...map("grossProfit"),
        subRows: [
          {
            label: "COGS",
            ...map("cogs"),
          },
          {
            label: "Carrier and Fulfillment Fees",
            ...map("shippingPaid"),
          },
          {
            label: "Payment Processing Fees",
            ...map("transactionFee"),
          },
          {
            label: "TOTAL GROSS PROFIT",
            meta: { highlight: true },
            ...map("grossProfit"),
          },
        ],
      },
      {
        label: "PROFIT CONTRIBUTION",
        meta: { color: "var(--primary)" },
        ...map("profitContribution"),
        subRows: [
          {
            label: "Direct Marketing",
            ...map("directMarketing"),
          },
          {
            label: "TOTAL PROFIT CONTRIBUTION",
            meta: { highlight: true },
            ...map("profitContribution"),
          },
        ],
      },
    ]
  );

const excelStyling = [
  {
    rows: [1],
    bold: true,
    border: "bottom",
  },
  {
    rows: [2, 7, 11, 13],
    cellColor: "E3DFEB",
    border: "bottom",
  },
  {
    columns: ["A"],
    border: "right",
  },
];

// const newCards = [
//   {
//     title: "New Discount Rate",
//     accessor: (data) => {
//       return data.dr * 100 || 0;
//     },
//     format: numberFormat.percent,
//     description: "% discount redeemed by new customers",
//   },
//   {
//     title: "New AOV",
//     accessor: (data) => {
//       return data.aov || 0;
//     },
//     format: numberFormat.currency,
//     description: "$ net sales + shipping on each new order",
//   },
//   {
//     title: "nCAC",
//     accessor: (data) => {
//       return data.directMarketing || 0;
//     },
//     format: numberFormat.currency,
//     description: "Acquisition cost per new customer",
//   },
//   {
//     title: "aMER (0-Day LTR:nCAC)",
//     accessor: (data) => {
//       return (data.aov || 0) / (data.directMarketing || 1);
//     },
//     format: numberFormat.currency,
//     description:
//       "Revenue return on the first purchase to customer acquisition cost",
//   },
// ];
// const returningCards = [
//   {
//     title: "Repeat Discount Rate",
//     accessor: (data) => {
//       return data.dr * 100 || 0;
//     },
//     format: numberFormat.percent,
//     description: "% discount redeemed by repeat customers",
//   },
//   {
//     title: "Repeat AOV",
//     accessor: (data) => {
//       return data.aov || 0;
//     },
//     format: numberFormat.currency,
//     description: "$ net sales + shipping on each repeat order",
//   },
//   {
//     title: "rCAC",
//     accessor: (data) => {
//       return data.directMarketing || 0;
//     },
//     format: numberFormat.currency,
//     description: "Acquisition cost per repeat customer",
//   },
// ];
// const summaryCards = [
//   {
//     title: "Gross Profit Margin",
//     accessor: (data) => {
//       return ((data.grossProfit || 0) / (data.aov || 1)) * 100;
//     },
//     format: numberFormat.percent,
//     description: "Gross profit % generated per order",
//   },
//   {
//     title: "Contribution Margin",
//     accessor: (data) => {
//       return ((data.profitContribution || 0) / (data.aov || 1)) * 100;
//     },
//     format: numberFormat.percent,
//     description: "$ net sales on each order",
//   },
// ];
//
// const UnitEconomicsSectionCards = ({ title, data, loading, cards }) => {
//   return (
//     <>
//       <DashboardLayout.SectionTitle className="col-span-1 @md:col-span-2 @5xl:col-span-4">
//         {title}
//       </DashboardLayout.SectionTitle>
//
//       {cards.map((card) => (
//         <KpiCard key={card.title} {...card} data={data} loading={loading} />
//       ))}
//     </>
//   );
// };

const waterfallLoadingData = () => {
  const grossRetail = Math.random() * 100000 + 100000;
  const dr = Math.random() * 0.1;
  const rr = Math.random() * 0.3;
  const shippingCollected = Math.random() * 10000 + 5000;
  const aov = grossRetail * (1 - dr - rr) + shippingCollected;
  const cogs = grossRetail * 0.3;
  const shippingPaid = shippingCollected * 0.5;
  const transactionFee = grossRetail * 0.02;
  const directMarketing = Math.random() * 10000 + 5000;
  const grossProfit = aov - cogs - shippingPaid - transactionFee;

  return {
    grossRetail,
    dr,
    rr,
    shippingCollected,
    aov,
    cogs,
    shippingPaid,
    transactionFee,
    directMarketing,
    grossProfit,
    profitContribution: grossProfit - directMarketing,
  };
};

const UnitEconomics = () => {
  const analytics = useAnalytics(
    unitEconomicsAtom,
    UNIT_ECONOMICS_QUERY,
    "unitEconomics",
    ({ data, periods, dateMin }) => {
      // fill in any missing periods past the min date.
      let missingData = [];
      [true, false, null].forEach((returningCustomer) => {
        missingData.push(
          ...periods.reduce((acc, curr) => {
            if (
              new Date(curr) >= dateMin &&
              !data.find(
                (d) =>
                  d.period === curr && d.returningCustomer === returningCustomer
              )
            ) {
              let toAdd = {
                period: curr,
                returningCustomer,
              };
              if (returningCustomer === null) {
                const existing = data.find((d) => d.period === curr);
                toAdd = existing ? { ...existing, returningCustomer } : toAdd;
              }

              acc.push(toAdd);
            }
            return acc;
          }, [])
        );
      });

      return [...data, ...missingData];
    }
  );
  const { store, setStore, refreshData, selectedIntegration, numberFormat } =
    analytics;
  const mapToRows = useMemo(
    () => createRowMapper(numberFormat),
    [numberFormat]
  );
  const { summary = true, resolution = "month" } = store;

  // const returningCustomerData = store.data?.find(
  //   (d) => d.period === "Summary" && d.returningCustomer === true
  // );
  // const newCustomerData = store.data?.find(
  //   (d) => d.period === "Summary" && d.returningCustomer === false
  // );
  const summaryData = store.data?.find(
    (d) => d.period === "Summary" && d.returningCustomer === null
  );

  const waterfallData = useMemo(() => {
    const data = summaryData || waterfallLoadingData();
    const chartData = [];

    chartData.push({
      name: "Gross Retail",
      top: data.grossRetail,
      bottom: 0,
      color: "var(--chart-good)",
    });
    chartData.push({
      name: "Discount",
      top: -data.dr * data.grossRetail || 0,
      bottom: data.grossRetail,
      color: "var(--chart-bad)",
    });
    chartData.push({
      name: "Refund",
      top: -data.rr * data.grossRetail || 0,
      bottom: data.grossRetail - data.dr * data.grossRetail,
      color: "var(--chart-bad)",
    });
    chartData.push({
      name: "Shipping Collected",
      top: data.shippingCollected,
      bottom:
        data.grossRetail -
        data.dr * data.grossRetail -
        data.rr * data.grossRetail,
      color: "var(--chart-good)",
    });
    chartData.push({
      name: "COGS",
      top: -data.cogs || 0,
      bottom: data.aov,
      color: "var(--chart-bad)",
    });
    chartData.push({
      name: "Shipping Paid",
      top: -data.shippingPaid || 0,
      bottom: data.aov - data.cogs,
      color: "var(--chart-bad)",
    });
    chartData.push({
      name: "Transaction Fee",
      top: -data.transactionFee || 0,
      bottom: data.aov - data.cogs - data.shippingPaid,
      color: "var(--chart-bad)",
    });
    chartData.push({
      name: "Direct Marketing",
      top: -data.directMarketing || 0,
      bottom: data.grossProfit,
      color: "var(--chart-bad)",
    });

    chartData.push({
      name: "Profit Contribution",
      top: data.profitContribution,
      bottom: 0,
      color: "var(--chart-good)",
    });

    return chartData;
  }, [summaryData]);

  const columns = useMemo(() => {
    const { periods, loading } = store;
    const activePeriods = (periods || getRecentMonths()).sort((a, b) =>
      a.localeCompare(b)
    );

    const cell = ({ getValue, row }) => {
      // hide value if parent row and is expanded
      if (
        row.getCanExpand() &&
        row.getIsExpanded() &&
        !row.original.meta?.alwaysShow
      ) {
        return null;
      }

      const format = row.original.format || numberFormat.currency;
      return (
        <div className={cn("text-right", row.original.bold && "font-bold")}>
          {format(getValue())}
        </div>
      );
    };

    if (summary) {
      return [
        colorGroupColumn,
        colorGroupLabelColumn,
        {
          header: "NEW",
          accessorKey: "summaryNew",
          meta: { className: "min-w-[100px] text-center" },
          cell,
        },
        {
          header: "RETURNING",
          accessorKey: "summaryReturning",
          meta: { className: "min-w-[100px] text-center" },
          cell,
        },
        {
          header: "ALL",
          accessorKey: "summaryAll",
          meta: { className: "min-w-[100px] text-center" },
          cell,
        },
      ];
    }

    return [
      colorGroupColumn,
      colorGroupLabelColumn,
      ...activePeriods.map((p) => {
        return {
          id:
            p +
            (loading ? "-loading" : "") +
            ("-" + store.returningCustomer?.join("_") || ""),
          header: styledDateHeader(p, resolution, store.dateFrom, store.dateTo),
          meta: { className: "min-w-[100px] text-right" },
          accessorKey: p,
          cell,
        };
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.periods, store.loading, summary, numberFormat]);

  const loadingData = useMemo(() => {
    // return some mock data while loading
    return mapToRows(
      getRecentMonths(),
      Array.from({ length: 12 }, (_, i) => ({
        period: new Date(Date.now() - i * 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 7),
      }))
    );
  }, [mapToRows]);

  const [tableData, exportTableData, tableHeaders] = useMemo(() => {
    // Normalize returningCustomer filter
    const returningCustomer =
      store.returningCustomer?.length === 1 ? store.returningCustomer[0] : null;

    // Prepare table headers for DataTable export
    // Skip the first column as it is a group color column
    const tableHeaders = columns.slice(1).map(({ header, accessorKey }) => ({
      header,
      accessorKey,
    }));

    let tableData;
    let exportTableData;
    if (store.data.length) {
      // transpose the data to match the expected format
      if (summary) {
        tableData = mapToRows(
          ["summaryNew", "summaryReturning", "summaryAll"],
          store.data
            .filter((d) => d.period === "Summary")
            .map((d) => {
              return {
                ...d,
                period:
                  "summary" +
                  (d.returningCustomer === true
                    ? "Returning"
                    : d.returningCustomer === false
                      ? "New"
                      : "All"),
              };
            })
        );
      } else {
        tableData = mapToRows(store.periods, store.data, returningCustomer);
      }

      // prepare export data
      exportTableData = getExcelExportData(
        tableData,
        tableHeaders,
        "label",
        numberFormat.currency
      );
      exportTableData.splice(1, 1); // remove AVERAGE ORDER VALUE
      exportTableData.splice(6, 1); // remove GROSS PROFIT
      exportTableData.splice(10, 1); // remove PROFIT CONTRIBUTION
    }

    return [tableData, exportTableData, tableHeaders];
  }, [
    columns,
    store.data,
    store.periods,
    store.returningCustomer,
    summary,
    mapToRows,
    numberFormat,
  ]);

  const imageExportRef = useRef(null);

  return (
    <>
      <DashboardLayout.Header onRefresh={refreshData} loading={store.loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters className="justify-between">
          <DateRangePicker
            useReportingInterval
            atom={unitEconomicsAtom}
            onSubmit={refreshData}
          />

          <DataExportButton
            disabled={store.loading}
            title="Unit Economics"
            integrationName={selectedIntegration?.description}
            dateFrom={store.dateFrom}
            dateTo={store.dateTo}
            tableData={exportTableData}
            tableHeaders={tableHeaders}
            imageExportRef={imageExportRef}
            styles={excelStyling}
          />
        </DashboardLayout.Filters>

        <div className="flex flex-col-reverse gap-8 sm:flex-row">
          <WaterfallChart
            className="w-full"
            containerClassName="h-[15rem] ![aspect-ratio:unset]"
            data={waterfallData}
            hasData={!!summaryData}
            loading={store.loading}
            dateFrom={store.dateFrom}
            dateTo={store.dateTo}
            title="Unit Economics"
            orientation="vertical"
            imageExportRef={imageExportRef}
          />

          {/* Tutorial Card */}
          <TutorialVideoCard {...tutorialVideos.unitEconomics} />
        </div>

        <DashboardLayout.TableOptions className="sm:justify-start">
          <Tabs
            defaultValue="summary"
            value={summary ? "summary" : "periodic"}
            onValueChange={(value) => {
              setStore({
                ...store,
                summary: value === "summary",
              });
            }}
          >
            <TabsList>
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="periodic">By {resolution}</TabsTrigger>
            </TabsList>
          </Tabs>

          {!summary && (
            <>
              <MetricsFilter
                title="Customer type"
                icon={UsersIcon}
                options={[
                  { label: "New", value: false },
                  { label: "Returning", value: true },
                ]}
                atom={unitEconomicsAtom}
                field="returningCustomer"
              />
            </>
          )}
        </DashboardLayout.TableOptions>

        <DataTable
          columns={columns}
          data={tableData}
          atom={unitEconomicsAtom}
          className="max-w-fit"
          loadingData={loadingData}
        />

        <DashboardLayout.Cards className="@md:grid-cols-2 @5xl:grid-cols-4">
          {/*<UnitEconomicsSectionCards*/}
          {/*  title="Acquisition Metrics"*/}
          {/*  cards={newCards}*/}
          {/*  data={newCustomerData}*/}
          {/*  loading={analytics.loading}*/}
          {/*/>*/}

          {/*<UnitEconomicsSectionCards*/}
          {/*  title="Retention Metrics"*/}
          {/*  cards={returningCards}*/}
          {/*  data={returningCustomerData}*/}
          {/*  loading={analytics.loading}*/}
          {/*/>*/}

          {/*<UnitEconomicsSectionCards*/}
          {/*  title="Profitability Metrics"*/}
          {/*  cards={summaryCards}*/}
          {/*  data={summaryData}*/}
          {/*  loading={analytics.loading}*/}
          {/*/>*/}
        </DashboardLayout.Cards>
      </DashboardLayout.Main>
    </>
  );
};

export default UnitEconomics;
